import type { AxiosInstance } from 'axios';

export type SponsoredPlacement =
  | 'HOMEPAGE_SHOWCASE'
  | 'HOMEPAGE_BANNER'
  | 'CATEGORY'
  | 'SEARCH'
  | 'PRODUCT'
  | 'VENDOR_PAID';

export type SponsoredTheme = 'DARK' | 'LUXURY' | 'ORGANIC' | 'FESTIVAL' | 'PREMIUM_GOLD' | 'CUSTOM';

export type CampaignStatusFilter = 'all' | 'active' | 'draft' | 'scheduled' | 'expired';

export interface SponsoredCollection {
  id: string;
  campaignId: string;
  collectionTitle: string;
  thumbnailImage: string;
  redirectUrl: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
}

export interface SponsoredCampaign {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  sponsorName: string;
  sponsorLogo: string | null;
  heroImage: string;
  productImage: string | null;
  ctaText: string;
  ctaUrl: string;
  couponCode: string | null;
  offerLabel: string | null;
  backgroundTheme: string;
  themeMeta: Record<string, unknown> | null;
  placement: SponsoredPlacement;
  vendorId: string | null;
  isActive: boolean;
  isFeatured: boolean;
  startDate: string | null;
  endDate: string | null;
  priority: number;
  totalViews: number;
  totalClicks: number;
  conversions: number;
  revenuePaise: number;
  createdAt: string;
  updatedAt: string;
  collections?: SponsoredCollection[];
}

export interface SponsoredCollectionInput {
  id?: string;
  collectionTitle: string;
  thumbnailImage: string;
  redirectUrl?: string;
  sortOrder?: number;
  isActive?: boolean;
}

export interface CreateSponsoredCampaignInput {
  title: string;
  subtitle?: string;
  slug?: string;
  sponsorName: string;
  sponsorLogo?: string;
  heroImage: string;
  productImage?: string;
  ctaText?: string;
  ctaUrl?: string;
  couponCode?: string;
  offerLabel?: string;
  backgroundTheme?: SponsoredTheme;
  themeMeta?: Record<string, unknown>;
  placement?: SponsoredPlacement;
  vendorId?: string;
  isActive?: boolean;
  isFeatured?: boolean;
  startDate?: string;
  endDate?: string;
  priority?: number;
  collections?: SponsoredCollectionInput[];
}

export type UpdateSponsoredCampaignInput = Partial<CreateSponsoredCampaignInput>;

export interface SponsoredAnalytics {
  totalCampaigns: number;
  totalViews: number;
  totalClicks: number;
  ctr: number;
  conversions: number;
  revenuePaise: number;
  topCampaigns: Array<{
    id: string;
    title: string;
    sponsorName: string;
    totalViews: number;
    totalClicks: number;
    conversions: number;
    revenuePaise: number;
    ctr: number;
  }>;
}

const unwrap = <T>(p: Promise<{ data: { data: T } }>) => p.then((r) => r.data.data);

export function sponsoredEndpoints(http: AxiosInstance) {
  return {
    // Storefront
    listActive: () => unwrap<SponsoredCampaign[]>(http.get('/sponsored-campaigns/active')),
    listFeatured: () => unwrap<SponsoredCampaign[]>(http.get('/sponsored-campaigns/featured')),
    getBySlug: (slug: string) =>
      unwrap<SponsoredCampaign>(http.get(`/sponsored-campaigns/${slug}`)),
    trackImpression: (id: string) =>
      http.post(`/sponsored-campaigns/${id}/impression`).then((r) => r.data),
    trackClick: (id: string) => http.post(`/sponsored-campaigns/${id}/click`).then((r) => r.data),

    // Admin
    listAdmin: (params?: {
      status?: CampaignStatusFilter;
      placement?: SponsoredPlacement;
      limit?: number;
    }) => unwrap<SponsoredCampaign[]>(http.get('/admin/sponsored-campaigns', { params })),
    get: (id: string) => unwrap<SponsoredCampaign>(http.get(`/admin/sponsored-campaigns/${id}`)),
    create: (body: CreateSponsoredCampaignInput) =>
      unwrap<SponsoredCampaign>(http.post('/admin/sponsored-campaigns', body)),
    update: (id: string, body: UpdateSponsoredCampaignInput) =>
      unwrap<SponsoredCampaign>(http.patch(`/admin/sponsored-campaigns/${id}`, body)),
    remove: (id: string) => http.delete(`/admin/sponsored-campaigns/${id}`).then((r) => r.data),
    analytics: () => unwrap<SponsoredAnalytics>(http.get('/admin/sponsored-campaigns/analytics')),
  };
}

export type SponsoredEndpoints = ReturnType<typeof sponsoredEndpoints>;
